/**
 * CommentRulesSection — reglas de "comentario con palabra clave -> DM" por CUENTA.
 *
 * Vive dentro de la tarjeta de cada cuenta conectada en /canales (COMMENT-RULES-SCHEMA.md §10).
 * La configuración es POR CUENTA, no por bot: dos cuentas de Instagram pueden compartir el
 * mismo bot y tener reglas distintas. El `accountId` es la ÚNICA barrera de aislamiento —
 * no hay otra dimensión que separe las reglas de una cuenta de las de otra.
 *
 * Requiere bot asignado: `handoffToBot` ya asume que hay uno, y sin él la mitad de la
 * feature no tiene sentido.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { db, auth } from '../firebase';
import {
  collection, query, where, onSnapshot, doc, getDoc,
  addDoc, updateDoc, deleteDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { useOrganization } from '../context/OrganizationContext';
import { buildRulesForIndustry, getAvailableVerticals } from '../utils/commentTemplates';
import { normalizeCommentText } from '../utils/commentMatching';
import { toast } from './Toast';
import styles from './CommentRulesSection.module.css';

// Caps espejo de validCommentRule() en firestore.rules. Se validan ACÁ para mostrar el
// error en el formulario; las rules son la defensa real, esto es sólo para que el cliente
// vea qué está mal en vez de recibir un "permission denied" opaco.
const MAX_KEYWORDS = 20;
const MAX_KEYWORD_LEN = 40;
const MAX_DM_TEXT = 900;
const MAX_TEMPLATES = 5;
const MIN_COOLDOWN = 1;
const MAX_COOLDOWN = 168;

const EMPTY_RULE = {
  active: true,
  priority: 30,
  keywords: [],
  matchMode: 'contains',
  mediaScope: { type: 'all', mediaIds: [] },
  dm: { text: '' },
  publicReply: { enabled: true, mode: 'fixed', templates: [], fallbackTemplates: [] },
  handoffToBot: true,
  botId: null,
  cooldownHours: 24,
};

/** Lista editable de strings. El repo no tiene componente de chips; sigue el patrón de
 *  KnowledgeForm/AvailabilityConfig: input + agregar + filter para quitar. */
function StringList({ items, onChange, placeholder, max, maxLen, disabled }) {
  const [draft, setDraft] = useState('');
  const list = items || [];
  const add = () => {
    const v = draft.trim();
    if (!v || list.length >= max) return;
    if (maxLen && v.length > maxLen) return;
    if (list.includes(v)) { setDraft(''); return; }
    onChange([...list, v]);
    setDraft('');
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {list.map((it, i) => (
          <span key={`${it}-${i}`} style={{
            background: '#eef2f7', borderRadius: 12, padding: '2px 8px',
            fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            {it}
            {!disabled && (
              <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#c0392b', lineHeight: 1 }}>×</button>
            )}
          </span>
        ))}
        {list.length === 0 && <span style={{ fontSize: 13, color: '#888' }}>—</span>}
      </div>
      {!disabled && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder={placeholder} maxLength={maxLen}
            style={{ flex: 1, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }} />
          <button type="button" onClick={add} disabled={list.length >= max}
            style={{ padding: '4px 10px', fontSize: 13, cursor: list.length >= max ? 'not-allowed' : 'pointer' }}>+</button>
        </div>
      )}
      {list.length >= max && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{max} máx.</div>}
    </div>
  );
}

export default function CommentRulesSection({ account, bots, canManage }) {
  const { t } = useTranslation('channels');
  const { context } = useOrganization();
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState(null);          // null = aún no cargadas
  const [seedCase, setSeedCase] = useState(null);    // 1 | 2 | 3
  const [seedIndustry, setSeedIndustry] = useState('otros');
  const [editing, setEditing] = useState(null);      // regla en edición (flag `dirty`)
  const [seeded, setSeeded] = useState(null);        // null = aún no leído
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);

  const bot = (bots || []).find((b) => b.id === account.botId) || null;
  const botIndustry = bot?.config?.industry || null;
  const botName = bot?.config?.name || bot?.nombre || bot?.name || account.botId;

  // useMemo: un array literal se recrea en cada render y desestabilizaría las deps de
  // los efectos y del useCallback de abajo (re-suscribiría el listener en cada render).
  const ownerPath = useMemo(
    () => (context.mode === 'organization' && context.orgId
      ? ['organizations', context.orgId]
      : ['clientes', auth.currentUser?.uid]),
    [context.mode, context.orgId]
  );

  // Listener SÓLO cuando la sección está abierta: con N cuentas conectadas, suscribir
  // siempre abriría N listeners que nadie está mirando.
  useEffect(() => {
    if (!open || !account.botId) return undefined;
    const q = query(
      collection(db, ...ownerPath, 'commentRules'),
      where('accountId', '==', account.id)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRules(next);
        // El formulario vive en su propio estado para que el snapshot no pise lo que el
        // cliente escribe (patrón `dirty`). El reverso: tampoco se entera de que su regla
        // dejó de existir. Sin esto, guardar devuelve el error crudo de Firestore
        // ("No document to update: .../commentRules/<id>"). Se cierra con aviso; NO se
        // convierte en creación nueva: quien borró una regla no espera que guardar la
        // resucite con otro id.
        setEditing((cur) => {
          if (cur?.id && !next.some((r) => r.id === cur.id)) {
            toast.info(t('commentRules.ruleVanished'));
            return null;
          }
          return cur;
        });
      },
      (err) => { console.error('[CommentRules] listener:', err.code, err.message); setRules([]); }
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account.id, account.botId, context.orgId, context.mode, ownerPath, t]);

  // Determina el caso de precarga (§ tres casos). Sólo corre cuando hace falta:
  // abierta, con bot, sin reglas y sin flag. Lee el playbook ACTIVO para saber si el
  // flujo vino de una plantilla o es personalizado.
  useEffect(() => {
    let cancel = false;
    const run = async () => {
      if (!open || !bot || rules === null) { setSeedCase(null); return; }
      // El flag vive en commentRulesMeta/{accountId}, NO en el channelAccount: esa
      // colección es `allow write: if false` (server-only) y el intento de marcarla
      // fallaba en silencio, dejando la precarga lista para resucitar.
      let yaSembrada = seeded;
      if (yaSembrada === null) {
        try {
          const metaSnap = await getDoc(doc(db, ...ownerPath, 'commentRulesMeta', account.id));
          yaSembrada = metaSnap.exists() && metaSnap.data()?.seeded === true;
        } catch (e) { console.warn('[CommentRules] meta:', e.message); yaSembrada = false; }
        if (cancel) return;
        setSeeded(yaSembrada);
      }
      if (rules.length > 0 || yaSembrada) { setSeedCase(null); return; }
      const apid = bot.runtime?.activePlaybookId;
      let source = null;
      if (apid) {
        try {
          const pb = await getDoc(doc(db, ...ownerPath, 'bots', bot.id, 'playbooks', apid));
          source = pb.exists() ? (pb.data().metadata?.source || null) : null;
        } catch (e) { console.warn('[CommentRules] playbook:', e.message); }
      }
      if (cancel) return;
      if (source === 'template' && botIndustry) {
        setSeedCase(1); setSeedIndustry(botIndustry);
      } else if (botIndustry) {
        // Flujo PERSONALIZADO: `industry` puede haber quedado de otra prueba y parecer
        // válido. Se preselecciona pero se PREGUNTA.
        setSeedCase(2); setSeedIndustry(botIndustry);
      } else {
        setSeedCase(3); setSeedIndustry('otros');
      }
    };
    run();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bot?.id, rules, seeded, account.id, botIndustry, ownerPath]);

  const seed = useCallback(async (industry) => {
    const docs = buildRulesForIndustry(industry, { accountId: account.id, canal: 'instagram' });
    if (!docs) { toast.error(t('commentRules.seedFailed')); return; }
    setBusy(true);
    // DOS escrituras con modos de fallo DISTINTOS, así que dos try separados. Antes un
    // solo catch pintaba todo como fallido aunque las reglas ya estuvieran creadas: el
    // cliente veía un error rojo sobre algo que había funcionado y volvía a apretar,
    // duplicando las reglas.
    let creadas = 0;
    try {
      const col = collection(db, ...ownerPath, 'commentRules');
      for (const d of docs) {
        await addDoc(col, {
          ...d,
          // Consistencia con el resto del repo: todos los docs llevan organizationId.
          organizationId: context.mode === 'organization' ? context.orgId : null,
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          createdByUid: auth.currentUser?.uid || null,
        });
        creadas += 1;
      }
    } catch (e) {
      console.error('[CommentRules] seed reglas:', e);
      toast.error(creadas === 0
        ? t('commentRules.seedFailed')
        : t('commentRules.seedPartial', { count: creadas }));
      setBusy(false);
      return;
    }

    // El FLAG es lo que impide que borrar todas las reglas las resucite. Inferirlo de
    // "cero reglas" haría que reaparecieran solas al recargar — un bug embrujado.
    try {
      await setDoc(doc(db, ...ownerPath, 'commentRulesMeta', account.id), {
        seeded: true,
        seededAt: serverTimestamp(),
        seededFromIndustry: industry,
        organizationId: context.mode === 'organization' ? context.orgId : null,
      });
      setSeeded(true);
      toast.success(t('commentRules.seeded', { count: creadas }));
    } catch (e) {
      // Las reglas SÍ existen. Decirlo explícito: si el mensaje dice "falló", el cliente
      // aprieta de nuevo y duplica.
      console.error('[CommentRules] seed flag:', e);
      setSeeded(true);   // en memoria, para no reofrecer la precarga en esta sesión
      // toast.info y NO toast.error: las reglas existen. Pintarlo de rojo es el mensaje
      // equivocado — es exactamente lo que hacía que el cliente reintentara y duplicara.
      toast.info(t('commentRules.seededNoFlag', { count: creadas }));
    } finally { setBusy(false); }
  }, [account.id, context.mode, context.orgId, ownerPath, t]);

  const validate = (r) => {
    if (!r.keywords?.length) return t('commentRules.errNoKeywords');
    if (r.keywords.length > MAX_KEYWORDS) return t('commentRules.errTooManyKeywords', { max: MAX_KEYWORDS });
    if (r.keywords.some((k) => k.length > MAX_KEYWORD_LEN)) return t('commentRules.errKeywordTooLong', { max: MAX_KEYWORD_LEN });
    if (!r.dm?.text?.trim()) return t('commentRules.errNoDmText');
    if (r.dm.text.length > MAX_DM_TEXT) return t('commentRules.errDmTooLong', { max: MAX_DM_TEXT });
    if ((r.publicReply?.templates || []).length > MAX_TEMPLATES) return t('commentRules.errTooManyTemplates', { max: MAX_TEMPLATES });
    const c = Number(r.cooldownHours);
    if (!Number.isInteger(c) || c < MIN_COOLDOWN || c > MAX_COOLDOWN) return t('commentRules.errCooldown', { min: MIN_COOLDOWN, max: MAX_COOLDOWN });
    return null;
  };

  const save = async () => {
    const err = validate(editing);
    if (err) { setFormError(err); return; }

    // §6 — LAS KEYWORDS SE PERSISTEN NORMALIZADAS. Esta ruta las guardaba crudas y el
    // sembrado (commentTemplates.js:227) no, así que de las dos escrituras sólo una cumplía
    // el contrato de commentMatching. El runtime compara contra texto ya normalizado
    // (minúsculas, sin tildes) con una RegExp SIN flag `i`, así que una keyword con una sola
    // mayúscula no podía matchear NUNCA. Coste real medido: la única regla viva del proyecto
    // llevaba 8 comentarios y stats.matched=0 desde el 22-ago, sin un solo error en ningún log.
    //
    // DEDUPE: `StringList` (:55) sólo descarta la cadena EXACTA (`list.includes`), así que
    // "Precio" y "precio" convivían como dos chips; al normalizar colapsan en uno y hay que
    // quitar el repetido, o la regla guardaría la misma keyword dos veces.
    //
    // Se revalida DESPUÉS de normalizar: `validate()` mira las crudas, y una keyword como
    // "!!!" pasa esa validación y se normaliza a cadena vacía. Sin este segundo control se
    // guardaría una regla con `keywords: []` — activa y que no matchea nada, que es
    // exactamente el fallo silencioso que este commit cierra.
    const keywords = [...new Set((editing.keywords || []).map(normalizeCommentText).filter(Boolean))];
    if (!keywords.length) { setFormError(t('commentRules.errNoKeywords')); return; }

    setFormError(null); setBusy(true);
    try {
      const payload = {
        accountId: account.id, canal: 'instagram',
        active: editing.active, priority: Number(editing.priority) || 30,
        keywords, matchMode: editing.matchMode || 'contains',
        mediaScope: editing.mediaScope || { type: 'all', mediaIds: [] },
        dm: { text: editing.dm.text },
        publicReply: editing.publicReply,
        handoffToBot: editing.handoffToBot !== false,
        botId: editing.botId || null,
        cooldownHours: Number(editing.cooldownHours),
        organizationId: context.mode === 'organization' ? context.orgId : null,
        updatedAt: serverTimestamp(),
      };
      if (editing.id) {
        await updateDoc(doc(db, ...ownerPath, 'commentRules', editing.id), payload);
      } else {
        await addDoc(collection(db, ...ownerPath, 'commentRules'), {
          ...payload,
          stats: { matched: 0, sent: 0, failed: 0, lastMatchAt: null },
          createdAt: serverTimestamp(), createdByUid: auth.currentUser?.uid || null,
        });
      }
      setEditing(null);
      toast.success(t('commentRules.saved'));
    } catch (e) {
      console.error('[CommentRules] save:', e);
      // Las rules pueden rechazar por shape aunque la validación local pase (p.ej. un
      // campo que la UI no conoce). Se muestra en vez de fallar en silencio.
      setFormError(e.code === 'permission-denied' ? t('commentRules.errDenied') : e.message);
    } finally { setBusy(false); }
  };

  const toggleActive = async (r) => {
    try {
      await updateDoc(doc(db, ...ownerPath, 'commentRules', r.id), { active: !r.active, updatedAt: serverTimestamp() });
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(t('commentRules.confirmDelete'))) return;
    try {
      await deleteDoc(doc(db, ...ownerPath, 'commentRules', r.id));
      toast.success(t('commentRules.deleted'));
    } catch (e) { toast.error(e.message); }
  };

  const activeCount = (rules || []).filter((r) => r.active).length;

  // Desalineamiento: sólo para reglas SEMBRADAS. Una regla escrita a mano no tiene
  // vertical de origen que contradecir, y un banner permanente sobre reglas correctas
  // entrena a ignorar banners — entonces el banner real tampoco se ve.
  const misaligned = (rules || []).some(
    (r) => r.seededFromIndustry && botIndustry && r.seededFromIndustry !== botIndustry
  );

  return (
    <div style={{ borderBottom: '1px solid #eee', padding: '0 0.75rem 0.75rem' }}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                 padding: '0.5rem 0', cursor: 'pointer', fontSize: 14, color: '#333' }}>
        {open ? '▾' : '▸'} 💬 {t('commentRules.title')}
        {rules !== null && <span style={{ color: '#888' }}> · {t('commentRules.activeCount', { count: activeCount })}</span>}
      </button>

      {open && !account.botId && (
        <div style={{ fontSize: 13, color: '#666', padding: '0.5rem 0' }}>{t('commentRules.needsBot')}</div>
      )}

      {open && account.botId && (
        <div style={{ paddingTop: 4 }}>
          {misaligned && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 6,
                          padding: '8px 10px', fontSize: 13, marginBottom: 10 }}>
              ⚠️ {t('commentRules.misaligned', { bot: botName, industry: botIndustry })}
            </div>
          )}

          {rules === null && <div style={{ fontSize: 13, color: '#888' }}>{t('commentRules.loading')}</div>}

          {rules !== null && seedCase && (
            <div style={{ background: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>{t('commentRules.seedIntro')}</div>
              {seedCase === 1 ? (
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  {t('commentRules.seedCase1', { industry: t(`commentRules.verticals.${seedIndustry}`, seedIndustry) })}
                </div>
              ) : (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>
                    {seedCase === 2 ? t('commentRules.seedCase2') : t('commentRules.seedCase3')}
                  </div>
                  <select value={seedIndustry} onChange={(e) => setSeedIndustry(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: 13 }}>
                    {getAvailableVerticals().map((v) => (
                      <option key={v.id} value={v.id}>{t(`commentRules.verticals.${v.id}`, v.label)}</option>
                    ))}
                  </select>
                  {(bots || []).length === 1 && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>{t('commentRules.starterHint')}</div>
                  )}
                </div>
              )}
              {canManage && (
                <button type="button" onClick={() => seed(seedIndustry)} disabled={busy}
                  style={{ padding: '6px 12px', fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
                  {busy ? t('commentRules.seeding') : t('commentRules.seedBtn')}
                </button>
              )}
            </div>
          )}

          {rules !== null && !seedCase && (
            <>
              {rules.length === 0 && (
                <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{t('commentRules.emptyAfterSeed')}</div>
              )}
              {rules.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999)).map((r) => (
                <div key={r.id} className={styles.commentRuleRow}
                                style={{ display: 'flex', alignItems: 'center', gap: 8,
                                         padding: '6px 0', borderTop: '1px solid #f0f0f0', fontSize: 13 }}>
                  <span className={styles.commentRuleText}
                        style={{ opacity: r.active ? 1 : 0.5, flex: 1 }}>
                    <strong>{r.seededRuleKey ? t(`commentRules.ruleKeys.${r.seededRuleKey}`, r.seededRuleKey) : t('commentRules.customRule')}</strong>
                    <span style={{ color: '#888' }}> · {(r.keywords || []).slice(0, 4).join(', ')}{(r.keywords || []).length > 4 ? '…' : ''}</span>
                  </span>
                  {canManage && (
                    <>
                      <button type="button" onClick={() => toggleActive(r)}
                        style={{ fontSize: 12, padding: '2px 8px', cursor: 'pointer' }}>
                        {r.active ? t('commentRules.disable') : t('commentRules.enable')}
                      </button>
                      <button type="button" onClick={() => { setFormError(null); setEditing({ ...EMPTY_RULE, ...r }); }}
                        style={{ fontSize: 12, padding: '2px 8px', cursor: 'pointer' }}>{t('commentRules.edit')}</button>
                      <button type="button" onClick={() => remove(r)}
                        style={{ fontSize: 12, padding: '2px 8px', color: '#c0392b', cursor: 'pointer' }}>{t('commentRules.delete')}</button>
                    </>
                  )}
                </div>
              ))}
              {canManage && (
                <button type="button" onClick={() => { setFormError(null); setEditing({ ...EMPTY_RULE }); }}
                  style={{ marginTop: 8, padding: '4px 10px', fontSize: 13, cursor: 'pointer' }}>
                  + {t('commentRules.newRule')}
                </button>
              )}
            </>
          )}

          {editing && (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #d0d7de', borderRadius: 6, background: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                {editing.id ? t('commentRules.editingRule') : t('commentRules.newRule')}
              </div>

              <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{t('commentRules.keywords')}</label>
              <StringList items={editing.keywords} max={MAX_KEYWORDS} maxLen={MAX_KEYWORD_LEN}
                placeholder={t('commentRules.keywordsPlaceholder')}
                onChange={(v) => setEditing({ ...editing, keywords: v })} />

              <label style={{ fontSize: 13, display: 'block', margin: '10px 0 4px' }}>{t('commentRules.dmText')}</label>
              {/* §3.3 — el opener y el playbook del bot tienen que sonar coherentes. */}
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                {t('commentRules.dmHandoffHint', { bot: botName })}
              </div>
              <textarea value={editing.dm?.text || ''} maxLength={MAX_DM_TEXT} rows={3}
                onChange={(e) => setEditing({ ...editing, dm: { text: e.target.value } })}
                style={{ width: '100%', fontSize: 13, padding: 6, border: '1px solid #ccc', borderRadius: 4 }} />
              <div style={{ fontSize: 11, color: '#888' }}>{(editing.dm?.text || '').length}/{MAX_DM_TEXT}</div>

              <label style={{ fontSize: 13, display: 'block', margin: '10px 0 4px' }}>{t('commentRules.publicTemplates')}</label>
              <StringList items={editing.publicReply?.templates} max={MAX_TEMPLATES} maxLen={200}
                placeholder={t('commentRules.templatePlaceholder')}
                onChange={(v) => setEditing({ ...editing, publicReply: { ...editing.publicReply, templates: v } })} />

              <label style={{ fontSize: 13, display: 'block', margin: '10px 0 4px' }}>{t('commentRules.fallbackTemplates')}</label>
              <StringList items={editing.publicReply?.fallbackTemplates} max={MAX_TEMPLATES} maxLen={200}
                placeholder={t('commentRules.templatePlaceholder')}
                onChange={(v) => setEditing({ ...editing, publicReply: { ...editing.publicReply, fallbackTemplates: v } })} />

              <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                <label style={{ fontSize: 13 }}>
                  {t('commentRules.cooldown')}{' '}
                  <input type="number" min={MIN_COOLDOWN} max={MAX_COOLDOWN} value={editing.cooldownHours}
                    onChange={(e) => setEditing({ ...editing, cooldownHours: e.target.value })}
                    style={{ width: 70, fontSize: 13, padding: '2px 4px' }} />
                </label>
                <label style={{ fontSize: 13 }}>
                  {t('commentRules.priority')}{' '}
                  <input type="number" value={editing.priority}
                    onChange={(e) => setEditing({ ...editing, priority: e.target.value })}
                    style={{ width: 70, fontSize: 13, padding: '2px 4px' }} />
                </label>
              </div>

              {formError && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#c0392b' }}>⚠️ {formError}</div>
              )}

              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button type="button" onClick={save} disabled={busy}
                  style={{ padding: '5px 12px', fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
                  {busy ? t('commentRules.saving') : t('commentRules.save')}
                </button>
                <button type="button" onClick={() => { setEditing(null); setFormError(null); }}
                  style={{ padding: '5px 12px', fontSize: 13, cursor: 'pointer' }}>
                  {t('commentRules.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
